import React, { useEffect, useMemo, useState } from 'react';
import { DropzoneAreaBase, FileObject } from 'react-mui-dropzone';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormikContextType, useFormikContext } from 'formik';
import { Grid, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DocumentUpload } from 'assets/icons/DocumentUpload';
import { JBColors } from '@juliusbaer-gcrm/gcrm-frontend-components';
import DocumentFileRow from 'views/ContactNoteForm/components/DocumentUpload/DocumentFileRow';
import { EnvConfig } from 'config/envConfig';
import { ContactNoteDialog } from 'types/ContactNote';
import { User } from 'types/User';
import { GlobalState } from 'reducers/stateTypes';

//freshly uploaded files
export interface FileObjectWithDescription extends FileObject {
  description: string;
  attachmentSource?: AttachmentSource;
  onDemandRef?: string;
}

//the info sent to / received form the backend
export interface FileInfo {
  description?: string;
  name: string;
  id: string;
  b64Data: string;
  attachmentSource?: AttachmentSource;
  onDemandRef?: string;
  mimeType?: string;
  file?: any;
  error?: string;
  size?: number;
}

export enum AttachmentSource {
  DOCSTORE = 'DOCSTORE',
  ONDEMAND = 'ONDEMAND',
}

const useStyles = makeStyles(() => ({
  dropZone: {
    maxHeight: '96px',
    minHeight: '96px',
    backgroundColor: '#E5F2F6',
    border: 'none',
    borderRadius: 0,
    margin: '24px 0',
    padding: 0,
  },
  dropzoneText: {
    fontSize: '12px',
    fontColor: JBColors.support.greenSmoke[100],
    padding: 0,
    margin: '18px 0',
  },
  preview: {
    //width: '100%',
    //height: '100%',
    //item: 'true',
    xs: '12',
  },
  previewImg: {
    display: 'none',
  },
}));

/**
 * The drop zone component where the user can upload PDF and other documents.
 */
const DocumentDropZone = () => {
  const formikContext = useFormikContext();
  const classes = useStyles();
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);

  //The file objects received from the backend.
  const [oldFileObjects, setOldFileObjects] = useState<FileInfo[]>((formikContext as any).values['attachments'] || []);
  //The newly uploaded files. Need to distinguish due to different data structure and type.
  const [fileObjects, setFileObjects] = useState<FileObjectWithDescription[]>([]);

  //Creating a new list to retain the uploaded file for new uploading flow while retain the existing flow
  const mergeFileObjects = useMemo(() => {
    if (EnvConfig.config.newSubmissionStatus) {
      return (formikContext as FormikContextType<ContactNoteDialog>).values['attachments'];
    }
    return [];
  }, [
    EnvConfig.config.newSubmissionStatus,
    (formikContext as FormikContextType<ContactNoteDialog>).values.attachments,
  ]);
  const { t } = useTranslation();

  const disableDropzone = useMemo(() => {
    if (EnvConfig.config.newSubmissionStatus) {
      return user.hasUpdateQualifiedContact;
    }
    return false;
  }, [user.hasUpdateQualifiedContact]);

  //We use useEffect instead of the usual onChange setFieldValue, as the data we send to the backend differs from the data we need to show the files uploaded.
  useEffect(() => {
    if (!EnvConfig.config.newSubmissionStatus) {
      const newObjectsToSend = fileObjects.map((fO) => {
        return {
          description: fO.description,
          name: fO.file.name,
          b64Data: (fO.data as string)?.split(',')[1],
        };
      });
      const allFiles = oldFileObjects.concat(newObjectsToSend as FileInfo[]);
      formikContext.setFieldValue('attachments', allFiles);
    }
  }, [fileObjects, formikContext.isSubmitting, EnvConfig.config.newSubmissionStatus]);

  const renderUploadedList = () => {
    if (EnvConfig.config.newSubmissionStatus) {
      return (
        <>
          {mergeFileObjects?.length === 0 && (
            <Typography>{t('documents.noAttachments', 'No attachments documents.')}</Typography>
          )}
          {mergeFileObjects.map((fileObject: FileInfo, i) => (
            <DocumentFileRow
              key={i}
              index={i}
              fileObject={fileObject}
              fileObjects={mergeFileObjects}
              setFileObjects={(update) => formikContext.setFieldValue('attachments', update)}
              viewOnly={user.hasUpdateQualifiedContact}
            />
          ))}
        </>
      );
    } else {
      return (
        <>
          {fileObjects?.length === 0 && oldFileObjects.length === 0 && (
            <Typography>{t('documents.noAttachments', 'No attachments documents.')}</Typography>
          )}
          {oldFileObjects.map((oldFileObject: FileInfo, i: number) => (
            <DocumentFileRow
              key={i}
              index={i}
              fileObject={oldFileObject}
              fileObjects={oldFileObjects}
              setFileObjects={setOldFileObjects}
            />
          ))}

          {fileObjects.map((fileObject: FileObjectWithDescription, i: number) => (
            <DocumentFileRow
              key={i}
              index={i}
              fileObject={fileObject}
              fileObjects={fileObjects}
              setFileObjects={setFileObjects}
            />
          ))}
        </>
      );
    }
  };
  return (
    <Grid container spacing={2}>
      <Grid item md={12} lg={6}>
        <Typography style={{ marginBottom: '4px' }}>Documents</Typography>
        <DropzoneAreaBase
          fileObjects={fileObjects}
          clearOnUnmount={true}
          filesLimit={100}
          dropzoneClass={classes.dropZone}
          dropzoneParagraphClass={classes.dropzoneText}
          inputProps={{ 'test-id': 'document-upload-dropzone' } as React.HTMLProps<HTMLInputElement>}
          maxFileSize={10_000_000}
          Icon={DocumentUpload}
          showPreviews={false}
          showPreviewsInDropzone={false}
          dropzoneProps={{ noClick: disableDropzone, noDrag: disableDropzone }}
          dropzoneText={t('documents.dropzoneText', 'Drag documents here or browse')}
          onAdd={(newFileObjs: FileObject[]) => {
            if (EnvConfig.config.createContactNoteNewUiEnabled) {
              if (user.hasNoteCreationAccess || user.hasNoteEditAccess) {
                const newObjectsToSend = newFileObjs.map((newFile) => ({
                  description: newFile.file.name,
                  name: newFile.file.name,
                  b64Data: (newFile.data as string)?.split(',')[1],
                  ...newFile,
                }));
                const existingFiles = (formikContext as FormikContextType<ContactNoteDialog>).values['attachments'];
                const allFiles = existingFiles.concat(newObjectsToSend as unknown as FileInfo[]);
                formikContext.setFieldValue('attachments', allFiles);
              }
              return;
            } else {
              const formattedFiles: FileObjectWithDescription[] = newFileObjs.map((newFile) => ({
                ...newFile,
                description: newFile.file.name,
              }));
              setFileObjects([...fileObjects, ...formattedFiles]);
            }
          }}
          onDelete={(deletedFileObject: FileObject) => {
            if (EnvConfig.config.createContactNoteNewUiEnabled) {
              if (user.hasNoteCreationAccess || user.hasNoteEditAccess) {
                const existingFiles = (formikContext as FormikContextType<ContactNoteDialog>).values[
                  'attachments'
                ].filter((file) => file.file.name !== deletedFileObject.file.name);
                formikContext.setFieldValue('attachments', existingFiles);
              }
              return;
            } else {
              setFileObjects((prevFiles) => prevFiles.filter((file) => file.file.name !== deletedFileObject.file.name));
            }
          }}
        />
      </Grid>

      <Grid item md={12} lg={6}>
        <Grid container>
          <Typography mb="4px" id="contactnotes-attached-docs-text">
            Attached documents
          </Typography>
          {!EnvConfig.config.createContactNoteNewUiEnabled && (
            <Tooltip
              title={t('documents.editTooltip', 'You can click the filename to edit it.')}
              placement="left-start"
              arrow
              id="document-attached-tooltip"
            >
              <InfoOutlinedIcon
                style={{
                  maxHeight: '12px',
                  color: 'grey',
                  marginTop: '1px',
                }}
              />
            </Tooltip>
          )}
        </Grid>
        <br />
        {renderUploadedList()}
      </Grid>
    </Grid>
  );
};

export default DocumentDropZone;
